/**
 * Vuetify component defaults Parcours adds on top of the Fondation's.
 *
 * Kept out of `main.ts` so the merge can be tested: that file mounts the app on import.
 */

/**
 * Components whose SELECTED state must be the theme's primary, not the near-black Vuetify falls
 * back to.
 *
 * Vuetify paints a selected element with `props.color` and, when none is given, with nothing at
 * all — the element then inherits `currentColor`, which is `on-surface` in the light theme. The
 * selected day of a date picker came out as a dark grey pill instead of a blue one; a ticked
 * checkbox does the same.
 *
 * A default rather than seventeen props, because the omission is invisible at the call site:
 * nothing about `<v-checkbox>` says it is about to render black. The rule the codebase already
 * follows — all 28 `v-switch` name a colour — simply becomes impossible to forget. An explicit
 * prop still wins, so the semantic colours (a warning switch, a success chip) are untouched.
 *
 * `primary` and not a hex: the `byrnuRed` theme must keep repainting these red the way it
 * repaints everything else.
 *
 * `VCheckboxBtn` alongside `VCheckbox`: the bare button is its own component and does not inherit
 * the wrapper's default — the Fondation renders some directly.
 *
 * Belongs in the Fondation's own `vuetifyOptions.defaults`, next to the `density`/`variant` block
 * it already posts for form fields (css-fondation#431); until then it lives here.
 */
export const SELECTION_DEFAULTS = {
  VDatePicker: { color: 'primary' },
  VCheckbox: { color: 'primary' },
  VCheckboxBtn: { color: 'primary' },
  VSwitch: { color: 'primary' },
  VBtnToggle: { color: 'primary' },
  VTabs: { color: 'primary' },
  VChipGroup: { color: 'primary' },
} as const;

/**
 * The Fondation's defaults with ours added.
 *
 * Merged per component and never wholesale: the Fondation posts `density`/`variant` for every
 * form field, and dropping that block would recreate the islands css-fondation#431 removed. A
 * component named on both sides keeps the Fondation's props and gains ours.
 */
export function mergeVuetifyDefaults(
  fondationDefaults: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...fondationDefaults };
  for (const [component, props] of Object.entries(SELECTION_DEFAULTS)) {
    const existing = merged[component];
    merged[component] = existing && typeof existing === 'object'
      ? { ...(existing as Record<string, unknown>), ...props }
      : { ...props };
  }
  return merged;
}
